import { Box, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Neutrals } from '../../../core-components/theme/color-palette/colors';
import { Button } from '../../../core-components/theme/components/Buttons';
import { visuallyHiddenSx } from '../../../core-components/theme/components/Fields/fieldStyles';
import { InlineAlert } from '../../../core-components/theme/components/InlineAlert';
import { StickyActionBar } from '../../../core-components/theme/components/StickyActionBar';
import { formatMmSs } from '../../../utilities/countdown';
import { useCountdown } from '../../../utilities/customHooks/useCountdown';
import { useFocusOnMount } from '../../../utilities/customHooks/useFocusOnMount';
import { useDeadlinePassed } from '../hooks/code/useDeadlinePassed';

import { AuthPanelHeading, authContentSx, authPanelSx } from './AuthPanel';
import { lockedText } from './refusalText';

const countdownSx: SystemStyleObject<Theme> = { color: Neutrals[900], fontVariantNumeric: 'tabular-nums' };

const LockCountdown: React.FC<{ readonly lockUntil: number }> = ({ lockUntil }) => {
  const { t } = useTranslation();
  const remaining = useCountdown(lockUntil);
  return (
    <Typography variant="title" component="p" sx={countdownSx}>
      <Box component="span" sx={visuallyHiddenSx}>
        {t('passcodeAuth.locked.timeLeft', 'Time left:')}{' '}
      </Box>
      {formatMmSs(remaining)}
    </Typography>
  );
};

interface LockedActionProps {
  readonly passed: boolean;
  readonly sending: boolean;
  readonly onBack: () => void;
  readonly onSendNew: () => void;
}

const BackButton: React.FC<{ readonly intent: 'primary' | 'secondary'; readonly onBack: () => void }> = ({ intent, onBack }) => {
  const { t } = useTranslation();
  return (
    <Button type="button" intent={intent} onClick={onBack}>
      {t('passcodeAuth.locked.back', 'Back')}
    </Button>
  );
};

const LockedDock: React.FC<LockedActionProps> = ({ passed, sending, onBack, onSendNew }) => {
  const { t } = useTranslation();
  if (!passed) return <StickyActionBar layout="dock" announce={false} primary={<BackButton intent="primary" onBack={onBack} />} />;
  return (
    <StickyActionBar
      layout="dock"
      announce={false}
      secondary={<BackButton intent="secondary" onBack={onBack} />}
      primary={
        <Button type="button" intent="primary" busy={sending} onClick={onSendNew}>
          {t('passcodeAuth.locked.sendNew', 'Send a new code')}
        </Button>
      }
    />
  );
};

export interface LockedPanelProps {
  readonly lockUntil: number;
  readonly lockMinutes: number;
  readonly sending: boolean;
  readonly onBack: () => void;
  readonly onSendNew: () => void;
}

export const LockedPanel: React.FC<LockedPanelProps> = ({ lockUntil, lockMinutes, sending, onBack, onSendNew }) => {
  const { t } = useTranslation();
  const titleRef = useFocusOnMount<HTMLHeadingElement>(true);
  const noticeId = useId();
  const passed = useDeadlinePassed(lockUntil);
  return (
    <Box sx={authPanelSx}>
      <Box sx={authContentSx}>
        <AuthPanelHeading title={t('passcodeAuth.locked.title', 'Too many attempts')} titleRef={titleRef} titleDescribedBy={noticeId}>
          <LockCountdown lockUntil={lockUntil} />
          <Box role="status" sx={visuallyHiddenSx}>
            {passed && t('passcodeAuth.locked.ready', 'You can try again now.')}
          </Box>
        </AuthPanelHeading>
        <InlineAlert id={noticeId} severity="warning" message={lockedText(t, lockMinutes)} />
      </Box>
      <LockedDock passed={passed} sending={sending} onBack={onBack} onSendNew={onSendNew} />
    </Box>
  );
};
